/** Composite key for the flat checkbox-state map. */
export function cellKey(frequencyId, categoryKey) {
    return `${frequencyId}::${categoryKey}`;
}
/** Every category key rendered as a row, in display order. */
export function displayedKeys(groups) {
    return groups.flatMap((group) => group.rows.map((row) => row.key));
}
/**
 * Initial state from the server-rendered props. `checkedKeys` already accounts for
 * `all_categories` (the facade expands it to every displayed key), so there is no second
 * place that has to remember what "all" means.
 */
export function initialCheckedState(frequencies) {
    const state = {};
    frequencies.forEach((frequency) => {
        frequency.checkedKeys.forEach((key) => {
            state[cellKey(frequency.frequencyId, key)] = true;
        });
    });
    return state;
}
export function checkedKeysFor(frequency, keys, checked) {
    return keys.filter((key) => checked[cellKey(frequency.frequencyId, key)]);
}
export function isColumnFullyChecked(frequency, keys, checked) {
    return keys.length > 0 && checkedKeysFor(frequency, keys, checked).length === keys.length;
}
export function isColumnEmpty(frequency, keys, checked) {
    return checkedKeysFor(frequency, keys, checked).length === 0;
}
export function isRowFullyChecked(categoryKey, frequencyIds, checked) {
    return frequencyIds.length > 0 && frequencyIds.every((id) => Boolean(checked[cellKey(id, categoryKey)]));
}
export function isRowUnchecked(categoryKey, frequencyIds, checked) {
    return frequencyIds.every((id) => !checked[cellKey(id, categoryKey)]);
}
export function isGroupFullyChecked(categoryKeys, frequencyIds, checked) {
    return categoryKeys.length > 0 && categoryKeys.every((key) => isRowFullyChecked(key, frequencyIds, checked));
}
export function isGroupUnchecked(categoryKeys, frequencyIds, checked) {
    return categoryKeys.every((key) => isRowUnchecked(key, frequencyIds, checked));
}
export function serializeScope({ attributeName, frequencies, keys, checked }) {
    const inputs = [];
    frequencies.forEach((frequency, frequencyIndex) => {
        const base = `${attributeName}[${frequencyIndex}]`;
        const categoryBase = `${base}[inspection_series_frequency_categories_attributes]`;
        const allChecked = isColumnFullyChecked(frequency, keys, checked);
        inputs.push({ name: `${base}[frequency_id]`, value: String(frequency.frequencyId) });
        inputs.push({ name: `${base}[all_categories]`, value: allChecked ? '1' : '0' });
        let rowIndex = 0;
        const pushDestroy = (id) => {
            inputs.push({ name: `${categoryBase}[${rowIndex}][id]`, value: String(id) });
            inputs.push({ name: `${categoryBase}[${rowIndex}][_destroy]`, value: '1' });
            rowIndex += 1;
        };
        if (allChecked) {
            // Rule 2 above.
            frequency.existingCategories.forEach((row) => pushDestroy(row.id));
            return;
        }
        // Rule 3 above.
        const displayed = new Set(keys);
        const existingKeys = new Set(frequency.existingCategories.map((row) => row.key));
        frequency.existingCategories.forEach((row) => {
            if (displayed.has(row.key) && !checked[cellKey(frequency.frequencyId, row.key)]) {
                pushDestroy(row.id);
            }
        });
        checkedKeysFor(frequency, keys, checked).forEach((key) => {
            if (existingKeys.has(key))
                return;
            inputs.push({ name: `${categoryBase}[${rowIndex}][category_key]`, value: key });
            rowIndex += 1;
        });
    });
    return inputs;
}
